import React, { useEffect, useRef, useState } from "react";
import { getStoredToken } from "../utils/auth";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const authHeaders = () => ({ "Content-Type": "application/json", token: getStoredToken() });

const requestJson = async (path, options = {}) => {
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: authHeaders(), ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.message || "Something went wrong");
  return data.result;
};

// Sandbox/live and the client id come from the API env
let configPromise = null;
export const loadPayPalConfig = () => {
  if (!configPromise) {
    configPromise = requestJson("/donator/paypal/config").catch((error) => {
      configPromise = null;
      throw error;
    });
  }
  return configPromise;
};

const sdkPromises = {};
const loadPayPalSdk = (sdkUrl) => {
  if (!sdkPromises[sdkUrl]) {
    sdkPromises[sdkUrl] = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = sdkUrl;
      script.async = true;
      script.onload = () => (window.paypal ? resolve(window.paypal) : reject(new Error("PayPal failed to load")));
      script.onerror = () => {
        delete sdkPromises[sdkUrl];
        script.remove();
        reject(new Error("PayPal failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return sdkPromises[sdkUrl];
};

const instancePromises = {};
const getSdkInstance = async (config) => {
  if (!instancePromises[config.client_id]) {
    instancePromises[config.client_id] = loadPayPalSdk(config.sdk_url)
      .then((paypal) =>
        paypal.createInstance({ clientId: config.client_id, components: ["paypal-payments"], pageType: "checkout" })
      )
      .catch((error) => {
        delete instancePromises[config.client_id];
        throw error;
      });
  }
  return instancePromises[config.client_id];
};

// PayPal Web SDK v6 one-time payment; the API creates and captures the order
const PayPalDonateButton = ({ postId, amount, disabled, onBusy, onSuccess, onPending, onError }) => {
  const buttonRef = useRef(null);
  const sessionRef = useRef(null);
  const latest = useRef({});
  latest.current = { postId, amount, disabled, onBusy, onSuccess, onPending, onError };
  const [state, setState] = useState("loading"); // loading | ready | unavailable

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await loadPayPalConfig();
        if (!config.enabled) throw new Error("PayPal is not available right now");
        const sdk = await getSdkInstance(config);
        const methods = await sdk.findEligibleMethods({ currencyCode: config.currency || "USD" });
        if (!methods.isEligible("paypal")) throw new Error("PayPal is not available right now");

        sessionRef.current = sdk.createPayPalOneTimePaymentSession({
          async onApprove({ orderId }) {
            try {
              const result = await requestJson("/donator/paypal/capture-order", {
                method: "POST",
                body: JSON.stringify({ order_id: orderId }),
              });
              if (result.pending) latest.current.onPending?.(result.message);
              else latest.current.onSuccess?.(result.donation);
            } catch (error) {
              latest.current.onError?.(error.message);
            } finally {
              latest.current.onBusy?.(false);
            }
          },
          onCancel() {
            latest.current.onBusy?.(false);
          },
          onError(error) {
            latest.current.onBusy?.(false);
            latest.current.onError?.(error?.message || "PayPal could not complete the payment");
          },
        });
        if (!cancelled) setState("ready");
      } catch (error) {
        console.error("PayPal setup failed:", error);
        if (!cancelled) setState("unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // start() must run inside the click, so nothing is awaited before it
  useEffect(() => {
    const button = buttonRef.current;
    if (!button || state !== "ready") return undefined;
    const handleClick = async () => {
      const { postId: currentPostId, amount: currentAmount, disabled: isDisabled } = latest.current;
      if (isDisabled || !sessionRef.current) return;
      latest.current.onBusy?.(true);
      const createOrderPromise = requestJson("/donator/paypal/create-order", {
        method: "POST",
        body: JSON.stringify({ post_id: Number(currentPostId), amount: Number(Number(currentAmount).toFixed(2)) }),
      }).then((result) => ({ orderId: result.order_id }));
      try {
        await sessionRef.current.start({ presentationMode: "auto" }, createOrderPromise);
      } catch (error) {
        latest.current.onBusy?.(false);
        const reason = await createOrderPromise.then(() => null).catch((orderError) => orderError.message);
        latest.current.onError?.(reason || error?.message || "Unable to start the PayPal payment");
      }
    };
    button.addEventListener("click", handleClick);
    return () => button.removeEventListener("click", handleClick);
  }, [state]);

  if (state === "unavailable") {
    return <p className="text-xs text-gray-500">PayPal is not available right now. Please choose another payment method.</p>;
  }

  return (
    <div className={disabled ? "pointer-events-none opacity-50" : ""}>
      {state === "loading" && <p className="text-xs text-gray-500">Loading PayPal...</p>}
      <paypal-button ref={buttonRef} type="pay" hidden={state !== "ready" ? true : undefined}></paypal-button>
    </div>
  );
};

export default PayPalDonateButton;
